import React from 'react';
import { ProfilePhotoUrls } from '@/types';

interface AvatarProps {
  /** User's structured profile photo URLs */
  profilePhotoUrls?: ProfilePhotoUrls | null;
  /** User's full name for generating initials */
  name: string;
  /** Avatar shape */
  shape?: 'round' | 'square';
  /** Avatar size */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Alt text for the image */
  alt?: string;
  /** Click handler */
  onClick?: () => void;
  /** Loading state */
  loading?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  profilePhotoUrls,
  name,
  shape = 'round',
  size = 'medium',
  className = '',
  style = {},
  alt,
  onClick,
  loading = false
}) => {

  // Determine which photo URL to use based on size
  const getPhotoUrl = (): string | null => {
    if (profilePhotoUrls) {
      // Use structured photo URLs based on avatar size
      switch (size) {
        case 'small':
          return profilePhotoUrls.thumbnail || profilePhotoUrls.small || profilePhotoUrls.medium || null;
        case 'medium':
          return profilePhotoUrls.small || profilePhotoUrls.medium || profilePhotoUrls.thumbnail || null;
        case 'large':
          return profilePhotoUrls.medium || profilePhotoUrls.large || profilePhotoUrls.small || null;
        case 'xlarge':
          return profilePhotoUrls.large || profilePhotoUrls.original || profilePhotoUrls.medium || null;
        default:
          return profilePhotoUrls.medium || profilePhotoUrls.small || profilePhotoUrls.thumbnail || null;
      }
    }
    
    // No photo URLs available
    return null;
  };

  const photoUrl = getPhotoUrl();
  
  // Generate initials from name
  const generateInitials = (fullName: string): string => {
    if (!fullName || fullName.trim() === '') return '?';
    
    const words = fullName.trim().split(/\s+/);
    
    if (words.length === 1) {
      // Single word: take first character
      return words[0].charAt(0).toUpperCase();
    } else {
      // Multiple words: take first character of first two words
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  };

  // Map sizes to CSS classes (without fixed text sizes)
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
    xlarge: 'w-24 h-24'
  };

  // Calculate font size based on container size
  const getFontSize = (): React.CSSProperties => {
    // If custom width is provided via className or style, use responsive sizing
    if (className.includes('w-full') || style.width) {
      return {
        fontSize: 'clamp(0.8rem, 6.4vw, 3.2rem)', // Responsive font size (20% smaller)
        lineHeight: '1'
      };
    }
    
    // Default sizes based on predefined size prop (20% smaller)
    const fontSizes = {
      small: '0.6rem',     // 9.6px (was 12px)
      medium: '0.8rem',    // 12.8px (was 16px)  
      large: '1rem',       // 16px (was 20px)
      xlarge: '1.2rem'     // 19.2px (was 24px)
    };
    
    return {
      fontSize: fontSizes[size],
      lineHeight: '1'
    };
  };

  // Map shapes to CSS classes
  const shapeClasses = {
    round: 'rounded-full',
    square: 'rounded-lg'
  };

  // Generate background color based on name (consistent color for same name)
  const generateBackgroundColor = (fullName: string): string => {
    const colors = [
      'bg-red-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    
    // Simple hash function to get consistent color
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = generateInitials(name);
  const backgroundColor = generateBackgroundColor(name);
      const dynamicFontSize = getFontSize();
  
  // Check if custom dimensions are provided via style
  const hasCustomDimensions = style.width || style.height;
  
  // When custom dimensions are provided, force round shape and don't use size classes
  const effectiveShape = hasCustomDimensions ? 'round' : shape;
  const sizeClassToUse = hasCustomDimensions ? '' : sizeClasses[size];
  
  // Calculate border-radius for custom dimensions
  const getCustomBorderRadius = (): React.CSSProperties => {
    if (hasCustomDimensions && style.width) {
      const width = typeof style.width === 'string' 
        ? parseInt(style.width.replace('px', '')) 
        : style.width;
      return { borderRadius: `${width / 2}px` };
    }
    return {};
  };
  
  const baseClasses = `
    flex items-center justify-center 
    ${sizeClassToUse} 
    ${hasCustomDimensions ? '' : shapeClasses[effectiveShape]}
    ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
    ${className}
  `.trim();

  // Combine dynamic font size with provided styles and custom border radius
  const combinedStyles = {
    ...dynamicFontSize,
    ...style,
    ...getCustomBorderRadius(),
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.3)'
  };

  // Loading state
  if (loading) {
    return (
      <div 
        className={`${baseClasses} bg-gray-300 animate-pulse`}
        style={combinedStyles}
        onClick={onClick}
      >
        <div className="w-full h-full bg-gray-400 rounded-inherit"></div>
      </div>
    );
  }

  // Photo available
  if (photoUrl) {
    return (
      <div className={baseClasses} style={combinedStyles} onClick={onClick}>
        <img
          src={photoUrl}
          alt={alt || `${name}'s avatar`}
          className={`w-full h-full object-cover ${hasCustomDimensions ? '' : shapeClasses[effectiveShape]}`}
          style={hasCustomDimensions ? getCustomBorderRadius() : {}}
          onError={(e) => {
            // Hide the image and show initials if image fails to load
            e.currentTarget.style.display = 'none';
            const initialsDiv = e.currentTarget.nextElementSibling as HTMLElement;
            if (initialsDiv) {
              initialsDiv.style.display = 'flex';
              // Apply font size to the fallback initials div
              Object.assign(initialsDiv.style, dynamicFontSize);
            }
          }}
        />
        <div 
          className={`w-full h-full ${backgroundColor} text-white font-semibold items-center justify-center ${hasCustomDimensions ? '' : shapeClasses[effectiveShape]} hidden`}
          style={hasCustomDimensions ? getCustomBorderRadius() : {}}
        >
          {initials}
        </div>
      </div>
    );
  }

  // No photo - show initials
  return (
    <div 
      className={`${baseClasses} ${backgroundColor} text-white font-semibold`}
      style={combinedStyles}
      onClick={onClick}
    >
      {initials}
    </div>
  );
};

export default React.memo(Avatar); 